import * as errors from '@attain-apps/errors'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as node from '../node'

import type { Edge } from './edge'
import { ForConnection } from './for-connection'
import { ForConsumption } from './for-consumption'
import type { Tracker } from './tracker'

export class List<TEdge extends Edge = Edge> {
  public get atLeastOne(): boolean {
    return this.edges.length >= 1
  }

  /**
   * If there is one edge in the list, this is that edge. If there are no
   * edges, this is `undefined`. If there is more than one edge, an error is
   * thrown.
   *
   * This is useful when up to one edge is expected from a certain list.
   */
  public get edge(): TEdge | undefined {
    if (this._edges.length > 1) {
      throw new errors.ValidationError({
        reason: 'Did not expect to have more than one',
        type: `number of '${this._type}' edges${this._node ? ` on node '${this._node.id}'` : ''}`,
        value: this._edges.length,
      })
    }
    return this._edges[0]
  }
  public get edges(): readonly TEdge[] {
    return this._edges
  }

  private readonly _edges: TEdge[] = []
  private get _counterpartType(): List.Type<TEdge> {
    // TODO: try to avoid casting
    return List.Type.getCounterpart<TEdge>(this._type)
  }
  constructor(
    /**
     * Represents the node that these edges are associated with. Certain
     * types of nodes do not support certain types of edges. In those cases,
     * we still create the list for that type of edge, but we leave this
     * property as undefined and then we never add edges to the list. This
     * just makes it easier for the consumer to process the nodes and edges,
     * since the consumer never has to check if a certain edge list is
     * undefined.
     */
    private readonly _node: TEdge['node'] | undefined,
    private readonly _type: List.Type<TEdge>,
  ) {}

  public has(edge: TEdge): boolean {
    const str = this._stringify(edge)
    return this._edges.some(_edge => this._stringify(_edge) === str)
  }

  public add(...edges: TEdge[]): void {
    if (!this._node) {
      // See this._node
      return
    }
    for (const edge of edges) {
      if (!this.has(edge)) {
        this._edges.push(edge)
        // NOTE: we don't `continue` here because if for whatever reason the
        // counterpart is missing, we want to add it
      }
      // NOTE: the counterpart list accepts the same type of edge (and thus
      // node) as this list, but TypeScript doesn't understand that, so we
      // need to cast. Otherwise, we get an error complaining about the type
      // of node
      const counterpartList = edge.node.edges[
        this._counterpartType
      ] satisfies List<Edge> as List<Edge> as typeof this
      const counterpartEdge = {
        ...edge,
        node: this._node,
      }
      // NOTE: this ensures that we don't get into an infinite loop of this
      // and the counterpartList adding back and forth to each other
      if (!counterpartList.has(counterpartEdge)) {
        counterpartList.add(counterpartEdge)
      }
    }
  }

  public remove(...edges: TEdge[]): void {
    if (!this._node) {
      // See this._node
      return
    }
    for (const edge of edges) {
      if (this.has(edge)) {
        this._edges.splice(this._edges.indexOf(edge), 1)
      }
      // NOTE: the counterpart list accepts the same type of edge (and thus
      // node) as this list, but TypeScript doesn't understand that, so we
      // need to cast. Otherwise, we get an error complaining about the type
      // of node
      const counterpartList = edge.node.edges[
        this._counterpartType
      ] satisfies List<Edge> as List<Edge> as typeof this
      const counterpartEdge = {
        ...edge,
        node: this._node,
      }
      // NOTE: this ensures that we don't get into an infinite loop of this
      // and the counterpartList adding back and forth to each other
      if (counterpartList.has(counterpartEdge)) {
        counterpartList.remove(counterpartEdge)
      }
    }
  }

  public clear(): void {
    for (const edge of this.edges) {
      this.remove(edge)
    }
  }

  /**
   * Finds shortest path (i.e. series of edges) between two nodes using
   * {@link https://en.wikipedia.org/wiki/Breadth-first_search breadth-first search}.
   *
   * If no such path exists, `undefined` is returned
   */

  public findShortestPathTo(
    filter: node.Identifiable | ((edge: TEdge) => boolean),
  ): TEdge[] | undefined {
    const filterFn = TypeChecker.isFunction(filter)
      ? filter
      : (edge: TEdge) => edge.node.id === node.Id.from(filter)
    const paths: TEdge[][] = this._edges.map(edge => [edge])

    let path: TEdge[] | undefined
    while ((path = paths.pop())) {
      const lastEdge = path[path.length - 1]
      if (filterFn(lastEdge)) {
        return path
      }

      for (const edge of lastEdge.node.edges[this._type].edges) {
        // NOTE: casting is needed because TypeScript doesn't understand
        // that every list of `this._type` will have the same TEdge
        if (!path.includes(edge as Edge as TEdge)) {
          paths.push([...path, edge as Edge as TEdge])
        }
      }
    }

    return undefined
  }
  /**
   * @see {@link findShortestPathTo}
   */
  public findFirstNodeWhere(
    filter: (edge: TEdge) => boolean,
  ): TEdge['node'] | undefined {
    const path = this.findShortestPathTo(filter)
    return path?.[path.length - 1].node
  }

  /**
   * __NOTE:__ this may not be the best implementation, but it is a quick
   * and dirty way to stringify the given edge. The "dirty" part is that we
   * have to cast `edge`
   */
  private _stringify(edge: TEdge): string {
    switch (this._type) {
      case 'connectedBy':
      case 'connecting':
        return ForConnection.stringify(edge as Edge as ForConnection)
      case 'consumedBy':
      case 'consuming':
        return ForConsumption.stringify(edge as Edge as ForConsumption)
    }
  }
}
export namespace List {
  export type Type<TEdge extends Edge = Edge> = Key.That.Extends<
    List<TEdge>,
    Tracker
  >
  export namespace Type {
    export function getCounterpart<TEdge extends Edge>(
      type: Type<TEdge>,
    ): Type<TEdge>
    export function getCounterpart(type: Type): Type {
      switch (type) {
        case 'connectedBy':
          return 'connecting'
        case 'connecting':
          return 'connectedBy'
        case 'consumedBy':
          return 'consuming'
        case 'consuming':
          return 'consumedBy'
        default:
          throw TypeAsserter.assertIsNever(type)
      }
    }
  }
}
